import CloseIcon from '@mui/icons-material/Close';
import { IconButton, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { selectCurrentTaskAIStatus } from '../../redux/features/taskRouter/taskRouter.selector';
import {
  selectEventAPI,
  selectEventAPIAuth,
} from '../../redux/features/config/config.selector';
import { selectActiveRestaurantCode } from '../../selectors/restaurant';
import {
  selectInProgressHypothesisTextFrames,
  selectIsWebSocketConnected,
} from '../../selectors/message';
import { selectUserProfile } from '../../selectors/user';
import { UserDetails } from '../../types';
import Colors from '../../utils/color';
import ConfirmDialog from '../common/ConfirmDialog';
import { MessageContainer } from '../Chat/MessageContainer';
import useTranscriptMessages from '../TranscriptSliderArea/useTranscriptMessages.hooks';
import { sendAsrIncorrectEvent } from '../../utils/asrEvents';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  // Transcript section: heading + inner white card. The grey card backdrop is
  // the shared left-pane card (MainPageLayout), so this stays transparent. The
  // takeover expansion is applied to that card (not this section), so the
  // reminder + AI status sections stay visible alongside the transcript.
  section: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(1),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(0.75),
  },
  title: {
    color: Colors.readBack,
    fontWeight: 700,
    fontSize: '0.8rem',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  liveDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
  },
  '@keyframes liveDotBlink': {
    '0%, 100%': { opacity: 1, boxShadow: `0 0 0 0 ${Colors.green}66` },
    '50%': { opacity: 0.3, boxShadow: `0 0 0 3px ${Colors.green}00` },
  },
  // Applied while the customer is speaking. Static $keyframes reference (JSS
  // only resolves the scoped name in a static rule, not a fn value).
  liveDotBlinking: {
    animation: '$liveDotBlink 1s ease-in-out infinite',
  },
  // Inner card holds the actual transcript messages.
  innerCard: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: Colors.white,
    border: `${spacing(0.125)} solid ${Colors.alto}`,
    borderRadius: spacing(1),
    overflow: 'hidden',
  },
  messages: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: spacing(1),
  },
  dividerContainer: {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: spacing(1),
  },
  divider: {
    fontSize: '14px',
    lineHeight: '20px',
    color: Colors.guardsmanRed,
  },
  underline: {
    flex: 1,
    padding: '0.75px',
    backgroundColor: Colors.guardsmanRed,
    margin: '5px',
    display: 'inline-block',
  },
}));

/**
 * LIVE TRANSCRIPT (left pane). Outer card holds the heading (with live dot to
 * the left of the label) and an inner card that holds the AI/customer message
 * stream (supervised-agent divider + click-to-report ASR-missing flow).
 *
 * NOTE: the volume control has been moved out of here for now.
 */
interface LiveTranscriptCardProps {
  // The takeover expansion lives on the parent left card; this card only needs
  // to know whether to show its collapse control.
  isExpanded?: boolean;
  onCollapse?: () => void;
}

const LiveTranscriptCard = ({
  isExpanded,
  onCollapse,
}: LiveTranscriptCardProps) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const currentTaskAIStatus = useAppSelector(selectCurrentTaskAIStatus);
  const isConnected = useAppSelector(selectIsWebSocketConnected);
  const hypothesisFrames = useAppSelector(selectInProgressHypothesisTextFrames);
  // Customer is actively speaking when ASR partials are streaming in — blink
  // the live dot while that's happening.
  const isAudioActive = isConnected && hypothesisFrames.length > 0;

  const eventAPI = useAppSelector(selectEventAPI);
  const eventAPIAuth = useAppSelector(selectEventAPIAuth);
  const restaurantCode = useAppSelector(selectActiveRestaurantCode);
  const userDetails = useAppSelector(selectUserProfile) as UserDetails | null;
  const effectiveSessionId = useAppSelector(
    (state) =>
      state.taskRouter.taskAssignedSessionId ||
      state.sessionBoundary.sessionId ||
      state.sessionBoundary.endedSessionId ||
      state.messages.currentSessionId
  );

  const { messagesList, sessionBufferMessagesList } = useTranscriptMessages();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const handleTranscriptAreaClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement | null;
      if (
        typeof document !== 'undefined' &&
        document.querySelector('[role="dialog"]')
      ) {
        return;
      }
      if (
        target &&
        (target.closest('[data-word]') || target.closest('[role="dialog"]'))
      ) {
        return;
      }
      setConfirmDialogOpen(true);
    },
    []
  );

  const handleConfirmBlankClick = useCallback(() => {
    sendAsrIncorrectEvent({
      eventAPI,
      eventAPIAuth,
      restaurantCode,
      effectiveSessionId,
      userDetails,
      wordClicked: 'ASR MISSING',
      dispatch,
    });
    setConfirmDialogOpen(false);
  }, [
    eventAPI,
    eventAPIAuth,
    restaurantCode,
    effectiveSessionId,
    userDetails,
    dispatch,
  ]);

  return (
    <div className={classes.section}>
      <div className={classes.header}>
        <span
          className={`${classes.liveDot} ${
            isAudioActive ? classes.liveDotBlinking : ''
          }`}
          style={{
            backgroundColor: isConnected ? Colors.green : Colors.silverChalice,
          }}
        />
        <span className={classes.title}>Live Transcript</span>
        {isExpanded && (
          <IconButton
            aria-label="Collapse transcript"
            size="small"
            onClick={onCollapse}
            // sx (not makeStyles) so it beats MUI ButtonBase's position: relative.
            sx={{ position: 'absolute', top: 8, right: 8, p: 0.25, zIndex: 1 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </div>
      <div className={classes.innerCard}>
        <div className={classes.messages} onClick={handleTranscriptAreaClick}>
          {sessionBufferMessagesList.length > 0 && (
            <MessageContainer messageList={sessionBufferMessagesList} compact />
          )}
          {currentTaskAIStatus && sessionBufferMessagesList.length > 0 && (
            <div className={classes.dividerContainer}>
              <div className={classes.underline} />
              <div className={classes.divider}>With Supervised Agent</div>
              <div className={classes.underline} />
            </div>
          )}
          <MessageContainer messageList={messagesList} compact />
        </div>
      </div>
      <ConfirmDialog
        open={confirmDialogOpen}
        content={'Send ASR incorrect event for word: "ASR MISSING"?'}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={handleConfirmBlankClick}
        closeBtnText="Cancel"
        confirmBtnText="OK"
      />
    </div>
  );
};

export default LiveTranscriptCard;
